import { LoadingButton } from "@mui/lab";
import { useMutation } from "@tanstack/react-query";
import {
  AnimationComponent,
  ArrowRightIcon,
  Button,
  FormControl,
  FormControlLabel,
  GKClientDataGrid,
  GKModal,
  GKTextField,
  Grid2,
  Radio,
  RadioGroup,
  ReloadIcon,
  Show,
  Typography,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useAppSelector } from "store/store";
import { numberToCurrency } from "utils";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField } from "utils/helpers";

const ClientExecutionModal = ({
  open,
  setOpen,
  basketId,
}: {
  open: boolean;
  setOpen: (d: boolean) => void;
  basketId: any;
  portfolioId: any;
}) => {
  const { userData } = useAppSelector((d) => d.user);

  const [rebalanceWithFund, setRebalanceWithFund] = useState(false);

  // ===###===###===###===###===###===###===###===###===###===###===###===###
  const { data: basketData } = useFetchQuery({
    key: ["GET_BASKET_BY_ID", userData?.id],
    route: `/basket_publish/${basketId}/`,
    requestBody: {
      user: userData?.id,
    },
  });

  // ===###===###===###===###===###===###===###===###===###===###===###===###
  const form = useForm({
    defaultValues: {
      use_current_allocation:
        basketData?.data?.data?.rebalanceReferenceWeightage,
      alloc_type: "rebalance",
      allocation_by: "version",
      rebalance_using: "portfolioValue",
      weightageDistributionMethod: "PRORATA",
      alloc_version:
        basketData?.data?.data?.selectedVersion ||
        basketData?.data?.data?.primaryModel?.id ||
        0,
      responseData: [],
      orderId: null,
      portfolios: [
        {
          id: basketData?.data?.data?.portfolio?.id,
          cash: 0,
          extraStatus: [],
          exitWeightage: 0,
        },
      ],
    },
    values: {
      use_current_allocation:
        basketData?.data?.data?.rebalanceReferenceWeightage,
      alloc_type: "rebalance",
      allocation_by: "version",
      rebalance_using: "portfolioValue",
      weightageDistributionMethod: "PRORATA",
      alloc_version:
        basketData?.data?.data?.selectedVersion ||
        basketData?.data?.data?.primaryModel?.id ||
        0,
      responseData: [],
      orderId: null,
      portfolios: [
        {
          id: basketData?.data?.data?.portfolio?.id,
          cash: 0,
          extraStatus: [],
          exitWeightage: 0,
        },
      ],
    },
  });

  // ===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate, data, isLoading } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post(`/model_portfolio/${basketId}/allocation/`, {
        ...data,
      }),
    onSuccess: (response) => {
      form.setValue(
        "responseData",
        response?.data?.map((da: any) => da?.plan)?.flat(),
      );
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Something went wrong, please try again.",
      );
    },
  });

  // ===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate: createOrder, isLoading: createLoading } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post(
        `/model_portfolio/${basketId}/order-manager/`,
        {
          ...data,
          alloc_version:
            basketData?.data?.data?.selectedVersion ||
            basketData?.data?.data?.primaryModel?.id ||
            0,
        },
        {
          headers: {
            Authorization: `Token ${userData?.token}`,
            "x-gridkey-user-role": "client",
          },
        },
      ),
    onSuccess: (response) => {
      form.setValue("orderId", response?.data?.orderAggregatorId);

      const url = `${
        process.env.REACT_APP_BASE_URL
      }/broker-integration/order?token=${userData?.token}&userPhone=${
        userData?.phone
      }&is_lm=false&order_id=${form.getValues("orderId")}&portfolio_id=${
        basketData?.data?.data?.portfolio?.id
      }&logo=`;
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Something went wrong, please try again.",
      );
    },
  });
  // ===###===###===###===###===###===###===###===###===###===###===###===###
  const { mutate: handleGenerateOrder, isLoading: generateOrderLoading } =
    useMutation({
      mutationFn: (data: any) =>
        axiosInstance.post(`/basket_publish/${basketId}/generate-order/`, {
          ...data,
        }),
      onSuccess: (response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `OrderSheet.csv`);
        document.body.appendChild(link);
        link.click();
        toast.success("Your file is successfully downloaded.");
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong, please try again.",
        );
      },
    });

  // ===###===###===###===###===###===###===###===###===###===###===###===###
  const formSubmit = (values: any) => {
    mutate(values);
  };

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      width={form.getValues("responseData")?.length !== 0 ? "60%" : "30%"}
      maxHeight={"90%"}
      modalTitle="Rebalance Your Portfolio"
      footer={
        form.getValues("responseData")?.length === 0 && (
          <Button
            endIcon={<ArrowRightIcon />}
            variant="contained"
            type="submit"
            form="submit-form"
            loading={isLoading}
          >
            Next
          </Button>
        )
      }
    >
      <AnimationComponent>
        <form onSubmit={form.handleSubmit(formSubmit)} id="submit-form">
          <Show.When isTrue={form.getValues("responseData")?.length === 0}>
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <Typography fontSize={12} color="var(--neutral-500)">
                  Rishabh Shetty has made changes to the basket Altura Growth
                  Opportunities on 10 Oct 2025{" "}
                </Typography>
                <br />
                <Typography fontSize={12} color="var(--neutral-500)">
                  Please rebalance your portfolio to align it with the basket.
                  You can also add more funds to the portfolio as a part of this
                  rebalance.{" "}
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <FormControl>
                  <RadioGroup
                    onChange={(event) => {
                      setRebalanceWithFund(JSON.parse(event.target.value));
                    }}
                    value={rebalanceWithFund}
                  >
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label="Use Existing Holdings"
                    />
                    <FormControlLabel
                      value
                      control={<Radio />}
                      label="Add Additional Amount"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid2>
              {rebalanceWithFund && (
                <Grid2 size={12} display={"flex"}>
                  <Controller
                    name={`portfolios.${0}.cash`}
                    control={form.control}
                    render={({ field }) => (
                      <GKTextField
                        inputLabel="Add more funds for Rebalance"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(+e.target.value)}
                      />
                    )}
                  />
                </Grid2>
              )}
            </Grid2>
          </Show.When>
          <Show.When isTrue={form.getValues("responseData")?.length !== 0}>
            <Grid2 container spacing={2}>
              <Grid2
                container
                size={12}
                spacing={2}
                bgcolor={"var(--grey-bg-100)"}
                px={2}
                py={3}
                borderRadius={2}
              >
                <Grid2 size={5} display={"flex"} alignItems={"center"} gap={2}>
                  <Controller
                    name={`portfolios.${0}.cash`}
                    control={form.control}
                    render={({ field }) => (
                      <GKTextField
                        inputLabel="Add more funds for Rebalance"
                        {...field}
                        value={field.value || ""}
                        onChange={(e) => field.onChange(+e.target.value)}
                      />
                    )}
                  />
                  <Button
                    startIcon={<ReloadIcon />}
                    type="submit"
                    form="submit-form"
                  >
                    Recalculate
                  </Button>
                </Grid2>
                <Grid2 size={7} textAlign={"right"}>
                  <Typography fontSize={12} color="var(--neutral-500)">
                    Residual Amount
                  </Typography>
                  <Typography
                    fontSize={20}
                    fontWeight={600}
                    color="var(--neutral-700)"
                  >
                    {numberToCurrency(
                      data?.data[0]?.cash - data?.data[0]?.totalBuyValue,
                    )}
                  </Typography>
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <GKClientDataGrid
                  name="executeSheet"
                  disableRowSelectionOnClick
                  rows={form.getValues("responseData")}
                  getRowId={(row) => row?.gridCode}
                  headerComponent={
                    <Grid2 container justifyContent={"flex-end"} gap={1}>
                      <LoadingButton
                        loading={generateOrderLoading}
                        variant="outlined"
                        onClick={() => {
                          const reqData: any = form?.getValues();
                          delete reqData.responseData;

                          handleGenerateOrder(reqData);
                        }}
                      >
                        Generate Order Sheet
                      </LoadingButton>
                      <LoadingButton
                        loading={createLoading}
                        variant="contained"
                        onClick={() => {
                          const reqData: any = form?.getValues();

                          delete reqData?.responseData;
                          createOrder(reqData);
                        }}
                      >
                        Execute Order
                      </LoadingButton>
                    </Grid2>
                  }
                  columns={[
                    {
                      ...extraColumnsField("Company", "company", 0, 1),
                    },
                    {
                      ...extraColumnsField(
                        "Transaction Type",
                        "transaction type",
                        0,
                        1,
                      ),
                    },
                    {
                      ...extraColumnsField("Price", "cmp", 0, 1),
                      type: "number",
                    },
                    {
                      ...extraColumnsField("Quantity", "quantity", 0, 1),
                      type: "number",
                    },
                  ]}
                />
              </Grid2>
            </Grid2>
          </Show.When>
        </form>
      </AnimationComponent>
    </GKModal>
  );
};

export default ClientExecutionModal;
